import {
  Button,
  Heading,
  Spacer,
  Stack,
  Text as ChakraText,
  useDisclosure,
} from '@chakra-ui/react'
import React, { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CachePolicies, useFetch } from 'use-http'
import { BaseLayout } from '..'
import { colors } from '../../../ui-kit'
import { checkPermission, PermissionsEnum } from '../../../utils'
import { FeaturesEnum } from '../../../utils/features'
import CustomPlanCTA from '../../components/CustomPlan'
import CreateUpdateUserGroupModal from '../../components/Users/CreateUpdateUserGroupModal'
import UserGroupTable from '../../components/Users/UserGroupTable'
import useToast from '../../hooks/useToast'
import type { UserGroupResponse } from '../../interfaces'
import { UserContext } from '../../providers'
import { MainContentHeadingBox, MainContentWrapper } from '../Base/MainContent'

const UserGroupLayout = (): React.ReactElement => {
  const { user } = useContext(UserContext)

  const { t } = useTranslation()
  const toast = useToast()

  const {
    isOpen: isOpenCreateUserGroup,
    onOpen: onOpenCreateUserGroup,
    onClose: onCloseCreateUserGroup,
  } = useDisclosure()

  const [data, setData] = useState<UserGroupResponse[]>()

  const { request, loading } = useFetch<UserGroupResponse[]>({
    cachePolicy: CachePolicies.NO_CACHE,
  })

  const getData = async (): Promise<void> => {
    if (
      user?.clientNumber &&
      user.pricingPlan.featuresList.filter(feature =>
        [
          FeaturesEnum.USER_MANAGEMENT,
          FeaturesEnum.ACL_AND_COLLECTIONS,
        ].includes(feature as FeaturesEnum),
      ).length === 2
    ) {
      const response = await request.get(
        `/api/clients/${user.clientNumber}/groups`,
      )

      if (request.error) {
        toast({
          description: t(
            'error_fetching_data',
            'Error in fetching data. Try refreshing the page.',
          ),
          status: 'error',
          isClosable: true,
          duration: 5000,
          position: 'top-right',
          variant: 'outline',
        })
      } else {
        setData(response)
      }
    }
  }

  return (
    <BaseLayout>
      {user?.clientNumber &&
      user.pricingPlan.featuresList.filter(feature =>
        [
          FeaturesEnum.USER_MANAGEMENT,
          FeaturesEnum.ACL_AND_COLLECTIONS,
        ].includes(feature as FeaturesEnum),
      ).length === 2 ? (
        <MainContentHeadingBox>
          <Heading
            as="h2"
            size="md"
            fontWeight="semibold"
            color={colors.appDarkIndigo}
          >
            {t('user_groups', 'User groups')}
          </Heading>
          <Spacer />
          {checkPermission({
            user,
            permission: PermissionsEnum.SELF_TEAM_MANAGE,
          }) ? (
            <Button marginLeft={8} onClick={onOpenCreateUserGroup}>
              {t('create_group', 'Create group')}
            </Button>
          ) : null}
        </MainContentHeadingBox>
      ) : (
        <MainContentHeadingBox>
          <Stack direction={['column']} spacing={1}>
            <Heading
              as="h2"
              size="md"
              fontWeight="600"
              color={colors.appDarkIndigo}
            >
              {t('user_groups', 'User groups')}
            </Heading>
            <ChakraText color="appBattleshipGrey">
              {t(
                'user_groups_description',
                'Add restricted media library users to groups and manage their permissions on assets',
              )}
            </ChakraText>
          </Stack>
        </MainContentHeadingBox>
      )}
      <MainContentWrapper>
        {user?.pricingPlan.featuresList.filter(feature =>
          [
            FeaturesEnum.USER_MANAGEMENT,
            FeaturesEnum.ACL_AND_COLLECTIONS,
          ].includes(feature as FeaturesEnum),
        ).length === 2 ? (
          <UserGroupTable
            user={user}
            data={data}
            getData={getData}
            loading={loading}
          />
        ) : (
          <CustomPlanCTA
            feature={
              !user?.pricingPlan.featuresList.includes(
                FeaturesEnum.ACL_AND_COLLECTIONS,
              )
                ? FeaturesEnum.ACL_AND_COLLECTIONS
                : FeaturesEnum.USER_MANAGEMENT
            }
          />
        )}
      </MainContentWrapper>
      <CreateUpdateUserGroupModal
        isUpdate={false}
        user={user}
        isOpen={isOpenCreateUserGroup}
        onClose={onCloseCreateUserGroup}
        getData={getData}
      />
    </BaseLayout>
  )
}

export default UserGroupLayout
